import type { CancelMedicationItemType, MedicationPadItemType } from '@/components/Patient/components/PrescriptionPad';
import {
  getProductDisplayName,
  isMedicationOutOfService,
} from '@/components/Patient/components/PrescriptionPad/PrescriptionPad.utils';
import type { DispenseLimitsByFormulation } from '@/utils/dosageLimit/dispenseLimit';
import { MedicationPrescriptionStatus } from '@montugroup/prescription-contracts';
import type { PrescriptionHistoryState, PrescriptionHistoryStatePreviousItem, PrescriptionPadRules } from '../types';
import { revalidatePad } from './revalidatePad';

/**
 * Updates provided param `medicationPadItems` list with `updatedMedicationPadItem`
 * @param medicationPadItems - A list of medication pad items
 * @param updatedMedicationPadItem - Medication pad item to be updated within the list
 * @param previousItems - Previously prescribed items from prescription history
 * @param dispenseLimits - Set dispense limits, written into the dosage the alert validates and kept only for the
 *   formulations still on the pad
 * @param rules - Validation rules for the alerts
 * @returns A new copy of the `medicationPadItems` list updated with `updatedMedicationPadItem` in `updatedItems`,
 * including the `updatedItem`, and the set limits of the formulations still on the pad in `dispenseLimits`.
 * If the product id has changed, a reference is returned for the `previousProductId`
 */
export const getUpdateMedicationPadItems = (
  medicationPadItems: MedicationPadItemType[],
  updatedMedicationPadItem: MedicationPadItemType,
  previousItems: PrescriptionHistoryStatePreviousItem[],
  dispenseLimits: DispenseLimitsByFormulation,
  rules: PrescriptionPadRules,
): {
  updatedItems: MedicationPadItemType[];
  updatedItem: MedicationPadItemType | undefined;
  previousProductId: number;
  dispenseLimits: DispenseLimitsByFormulation;
} => {
  let previousProductId: number = -1;
  const isUpdatedItem = (item: MedicationPadItemType) =>
    item.medicationPadItemId === updatedMedicationPadItem.medicationPadItemId;
  const itemsAfterUpdate = medicationPadItems.map((item) => {
    if (!isUpdatedItem(item)) {
      return item;
    }
    previousProductId = item.medicationPadItem?.productId ?? previousProductId;
    const previousItem = previousItems.find(
      (previousItem) => previousItem?.id === updatedMedicationPadItem.medicationPadItem?.productId,
    );
    return {
      ...updatedMedicationPadItem,
      prescriptionStatus: previousItem?.prescriptionStatus ?? updatedMedicationPadItem?.prescriptionStatus,
      initialMedication: item.initialMedication ?? item.medicationPadItem ?? updatedMedicationPadItem.initialMedication,
      reissueStatus: previousItem?.reissueStatus,
    };
  });
  // Every alert reads the whole pad as it will stand after the update: the formulation's limit in force spans every
  // item, and is written into each sibling's dosage, so a sibling's dosage can grow past its limit on this update. A
  // product or filter swap can leave the formulation without a row
  const pad = revalidatePad({ medicationPadItems: itemsAfterUpdate, dispenseLimits }, rules);

  return {
    updatedItems: pad.medicationPadItems,
    updatedItem: pad.medicationPadItems.find(isUpdatedItem),
    previousProductId,
    dispenseLimits: pad.dispenseLimits,
  };
};

/**
 * Updates the reissue and cancel lists based on the new product selection.
 * @param updatedMedicationPadItem - The updated medication pad item object
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @returns Updated lists of reissued and cancelled items, and the updated medication pad item if the product is being reissued
 */
export const handleNewProductSelection = (
  updatedMedicationPadItem: MedicationPadItemType,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[],
) => {
  const { medicationPadItem } = updatedMedicationPadItem;
  const updatedProductId = medicationPadItem?.productId;
  let updatedReissuedItems = [...itemsInReissue];
  let updatedCancelledItems = [...itemsInCanceled];
  let reissuedMedication;
  if (updatedProductId !== undefined) {
    // is it in previously prescribed items?
    const previouslyPrescribedNewSelection = previousItems.find(
      (item) =>
        item.id === updatedProductId &&
        ![MedicationPrescriptionStatus.Deleted, MedicationPrescriptionStatus.Error].includes(item.prescriptionStatus),
    );
    if (previouslyPrescribedNewSelection) {
      reissuedMedication = {
        ...updatedMedicationPadItem,
        reissueStatus: previouslyPrescribedNewSelection.reissueStatus,
        prescriptionStatus: previouslyPrescribedNewSelection.prescriptionStatus,
      };

      // An out of service script already marked for cancellation stays cancelled when re-selected: it cannot be
      // issued as-is, so the doctor still has to move off it and the history row should not flip back to reissue
      const staysCancelled =
        isMedicationOutOfService(previouslyPrescribedNewSelection.reissueStatus) &&
        updatedCancelledItems.some((item) => item.productId === updatedProductId);
      if (staysCancelled) {
        return { updatedReissuedItems, updatedCancelledItems, updatedMedicationPadItem: reissuedMedication };
      }

      updatedReissuedItems = [...updatedReissuedItems, reissuedMedication];
    }
    // if new item is in cancelled, remove it
    updatedCancelledItems = updatedCancelledItems.filter((item) => item.productId !== updatedProductId);
  }
  return {
    updatedReissuedItems,
    updatedCancelledItems,
    updatedMedicationPadItem: reissuedMedication,
  };
};

/**
 * Updates the reissue and cancel lists based on the unselected product.
 * @param previousProductId - product Id of previous item selection
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @returns Updated lists of reissued and cancelled items
 */
export const handleUnselectedProduct = (
  previousProductId: number,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[],
) => {
  let updatedReissuedItems = [...itemsInReissue];
  let updatedCancelledItems = [...itemsInCanceled];
  if (typeof previousProductId === 'number' && previousProductId !== -1) {
    const previouslyPrescribedActivePreviousSelection = previousItems.find(
      (item) => item.id === previousProductId && item.prescriptionStatus === MedicationPrescriptionStatus.Active,
    );
    // handle reissue list: if previous item is currently in reissue, remove it
    updatedReissuedItems = updatedReissuedItems.filter(
      (item) => item.medicationPadItem?.productId !== previousProductId,
    );
    // handle cancel list: only add to cancel list if product was previously prescribed and currently active
    const alreadyCancelled = updatedCancelledItems.some((item) => item.productId === previousProductId);
    if (previouslyPrescribedActivePreviousSelection && !alreadyCancelled) {
      const previousItemInReissuedList = itemsInReissue.find(
        (item) => item.medicationPadItem?.productId === previousProductId,
      );

      updatedCancelledItems = [
        ...updatedCancelledItems,
        {
          productId: previousProductId,
          displayName: getProductDisplayName(
            previousItemInReissuedList?.medicationPadItem?.productName,
            previousItemInReissuedList?.medicationPadItem?.description,
            previousItemInReissuedList?.medicationPadItem?.supplierName ?? '',
          ),
        },
      ];
    }
  }
  return {
    updatedReissuedItems,
    updatedCancelledItems,
  };
};

/**
 * Updates the reissue and cancel lists when changing products.
 * @param updatedMedicationPadItem - The updated medication pad item object
 * @param previousItems - list of previously prescribed medications
 * @param itemsInReissue - list of medication items that are listed for reissue
 * @param itemsInCanceled - list of medication items that are listed for cancelation
 * @param previousProductId - product Id of previous item selection
 * @returns Updated lists of reissued and cancelled items, and the updated medication pad item if the product is being reissued
 */
export const updateReissueAndCancelWhenChangingProducts = (
  updatedMedicationPadItem: MedicationPadItemType,
  previousItems: PrescriptionHistoryState['previousItems'],
  itemsInReissue: MedicationPadItemType[],
  itemsInCanceled: CancelMedicationItemType[],
  previousProductId: number,
): {
  updatedCancelledItems: CancelMedicationItemType[];
  updatedReissuedItems: MedicationPadItemType[];
  updatedMedicationPadItem?: MedicationPadItemType;
} => {
  const { medicationPadItem } = updatedMedicationPadItem;
  const updatedProductId = medicationPadItem?.productId;

  const updatedReissuedItems = [...itemsInReissue];
  const updatedCancelledItems = [...itemsInCanceled];
  let reissuedMedication;
  if (previousProductId !== updatedProductId) {
    // handle new product
    const newProductResult = handleNewProductSelection(
      updatedMedicationPadItem,
      previousItems,
      updatedReissuedItems,
      updatedCancelledItems,
    );
    reissuedMedication = newProductResult.updatedMedicationPadItem;

    // handle previous product
    const unselectedProductResult = handleUnselectedProduct(
      previousProductId,
      previousItems,
      newProductResult.updatedReissuedItems,
      newProductResult.updatedCancelledItems,
    );

    return {
      updatedCancelledItems: unselectedProductResult.updatedCancelledItems,
      updatedReissuedItems: unselectedProductResult.updatedReissuedItems,
      updatedMedicationPadItem: reissuedMedication,
    };
  }

  return {
    updatedCancelledItems,
    updatedMedicationPadItem: reissuedMedication,
    updatedReissuedItems,
  };
};
